/**
 * PaymentReadinessBanner.jsx — Iter-315c · reworked 2026-09 · Iter-461 (payment hold)
 *
 * Persistent banner shown when a talent has outstanding post-shift EVENT
 * REQUIREMENTS (clock-out, recap, metrics, photos). Any outstanding requirement
 * now places owed payment ON HOLD until every item is completed AND verified.
 * The payment chip surfaces "On Hold: Incomplete Event Requirements" and the
 * hold releases automatically once all requirements clear and the timesheet is
 * approved.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, ArrowRight, ChevronDown, ChevronUp, CheckCircle2, CircleDollarSign, UserCheck } from 'lucide-react';
import api from '../utils/api';

const attendanceChip = (s) => {
  switch (s) {
    case 'verified': return { label: 'Attendance: Verified', cls: 'bg-emerald-100 text-emerald-700' };
    case 'worked': return { label: 'Attendance: Worked', cls: 'bg-emerald-100 text-emerald-700' };
    case 'needs_verification': return { label: 'Attendance: Needs Verification', cls: 'bg-amber-100 text-amber-700' };
    case 'no_show': return { label: 'Attendance: No-Show', cls: 'bg-rose-100 text-rose-700' };
    default: return { label: 'Attendance: Scheduled', cls: 'bg-slate-100 text-slate-600' };
  }
};

const paymentChip = (s) => {
  switch (s) {
    case 'paid': return { label: 'Payment: Paid', cls: 'bg-emerald-100 text-emerald-700' };
    case 'processing': return { label: 'Payment: Processing', cls: 'bg-sky-100 text-sky-700' };
    case 'on_hold': return { label: 'On Hold: Incomplete Event Requirements', cls: 'bg-rose-100 text-rose-700' };
    default: return { label: 'Payment: Pending', cls: 'bg-slate-100 text-slate-600' };
  }
};

const docChip = (s) => (s === 'complete'
  ? { label: 'Docs: Complete', cls: 'bg-emerald-100 text-emerald-700' }
  : { label: 'Docs: Incomplete', cls: 'bg-amber-100 text-amber-800' });

const Chip = ({ cfg, testid }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.cls}`} data-testid={testid}>
    {cfg.label}
  </span>
);

const PaymentReadinessBanner = () => {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/payment-readiness/my-blockers')
      .then((res) => { if (alive) setPayload(res.data); })
      .catch(() => { /* non-critical */ });
    return () => { alive = false; };
  }, []);

  if (!payload || !payload.any_action_required) return null;
  const items = payload.outstanding || [];
  if (items.length === 0) return null;

  const first = items[0];
  const firstBlocker = (first.talent_action_blockers || [])[0];
  const totalBlockerCount = items.reduce((sum, i) => sum + (i.talent_action_blockers || []).length, 0);

  const goCta = (url) => {
    if (!url) return;
    if (url.startsWith('http')) window.location.href = url;
    else navigate(url);
  };

  const goComplete = (bookingId) => navigate(`/talent/booking/${bookingId}?complete=1`);

  return (
    <div
      className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 shadow-sm"
      data-testid="payment-readiness-banner"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
          <ClipboardList className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold uppercase tracking-wide text-amber-900" data-testid="payment-readiness-heading">
            Event Documentation Incomplete
          </div>
          <div className="text-sm text-amber-800 mt-0.5">
            You have {totalBlockerCount} outstanding {totalBlockerCount === 1 ? 'item' : 'items'} to finish your
            event documentation on {items.length} {items.length === 1 ? 'shift' : 'shifts'}.
          </div>
          <div className="mt-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800" data-testid="payment-hold-notice">
            <span className="font-bold">Payment Hold:</span> You have outstanding event requirements.
            Any payment owed will be placed on hold until all required event documentation is completed and verified.
          </div>

          {firstBlocker && (
            <div className="mt-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <div className="min-w-0 flex-1 text-xs text-amber-900">
                <div className="font-semibold">{firstBlocker.label}</div>
                <div className="text-amber-700 truncate">
                  {first.event_title || 'Untitled event'}
                  {first.shift_date ? ` · ${first.shift_date}` : ''}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  <Chip cfg={attendanceChip(first.attendance_status)} testid="chip-attendance" />
                  <Chip cfg={paymentChip(first.payment_state)} testid="chip-payment" />
                  <Chip cfg={docChip(first.documentation_status)} testid="chip-docs" />
                </div>
              </div>
              <button
                type="button"
                onClick={() => goComplete(first.booking_id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg transition shadow"
                data-testid="payment-readiness-cta"
              >
                Complete Event Requirements
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {(totalBlockerCount > 1 || items.length > 1) && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 hover:text-amber-900"
              data-testid="payment-readiness-toggle"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? 'Hide details' : `Show all ${items.length} shifts`}
            </button>
          )}

          {expanded && (
            <ul className="mt-3 space-y-2" data-testid="payment-readiness-list">
              {items.map((booking) => (
                <li key={booking.booking_id} className="p-2.5 bg-white rounded-lg border border-amber-200">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-gray-900 truncate">
                      {booking.event_title || 'Untitled event'}
                      {booking.shift_date ? ` · ${booking.shift_date}` : ''}
                    </div>
                    <button
                      type="button"
                      onClick={() => goComplete(booking.booking_id)}
                      className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 whitespace-nowrap"
                      data-testid={`payment-readiness-complete-${booking.booking_id}`}
                    >
                      Complete →
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <Chip cfg={attendanceChip(booking.attendance_status)} />
                    <Chip cfg={paymentChip(booking.payment_state)} />
                    <Chip cfg={docChip(booking.documentation_status)} />
                  </div>
                  <ul className="mt-2 space-y-1">
                    {(booking.talent_action_blockers || []).map((b) => (
                      <li key={b.code} className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1 text-[11px] text-gray-700">
                          <span className="font-medium text-amber-800">{b.label}</span>
                          <span className="ml-1 text-gray-500">{b.message}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => goCta(b.cta_url)}
                          className="text-[11px] font-semibold text-amber-600 hover:text-amber-800 whitespace-nowrap"
                          data-testid={`payment-readiness-item-cta-${booking.booking_id}-${b.code}`}
                        >
                          {b.cta_label || 'Complete →'}
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentReadinessBanner;
